import { COLORS, API_URL, LOGO_URL } from './constants';
import { loadImageWithDimensions } from './utils';

let cachedLogoBase64 = null;

export const loadPDELogo = async () => {
  if (cachedLogoBase64) return cachedLogoBase64;
  try {
    const response = await fetch(LOGO_URL);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        cachedLogoBase64 = reader.result;
        resolve(cachedLogoBase64);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Failed to load PDE logo:', e);
    return null;
  }
};

const drawLogoFallback = (doc, x, y, scale) => {
  const size = 14 * scale;
  doc.setFillColor(...COLORS.anthracite);
  doc.roundedRect(x, y, size, size, 2.5 * scale, 2.5 * scale, 'F');
  doc.setDrawColor(...COLORS.azur);
  doc.setFillColor(...COLORS.azur);
  doc.setLineWidth(3 * scale);
  doc.setLineCap('round');
  doc.line(x + size * 0.15, y + size * 0.85, x + size * 0.80, y + size * 0.20);
};

export const drawLogo = async (doc, x, y, scale = 1) => {
  const logoHeight = 12 * scale;
  const logoWidth = logoHeight * 2.755;
  const logoBase64 = await loadPDELogo();
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'PNG', x, y, logoWidth, logoHeight, undefined, 'FAST');
    } catch (e) {
      drawLogoFallback(doc, x, y, scale);
    }
  } else {
    drawLogoFallback(doc, x, y, scale);
  }
  return x + logoWidth + 5 * scale;
};

export const drawPageNumber = (doc, pageNum, pageHeight) => {
  doc.setTextColor(...COLORS.pageNumberGray);
  doc.setFontSize(100);
  doc.setFont('Karla', 'bold');
  const numStr = pageNum < 10 ? `0${pageNum}` : `${pageNum}`;
  doc.text(numStr, 185, pageHeight - 35, { align: 'right' });
};

export const drawHeader = async (doc, report, margin, pageWidth) => {
  await drawLogo(doc, margin, 12, 1);
  if (report.client_logo) {
    try {
      let logoData;
      if (report.client_logo.startsWith('data:')) {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = report.client_logo;
        });
        logoData = { 
          base64: report.client_logo, 
          aspectRatio: img.width / img.height || 2.5
        };
      } else {
        const logoUrl = report.client_logo.startsWith('http') 
          ? report.client_logo 
          : `${API_URL}${report.client_logo}`;
        logoData = await loadImageWithDimensions(logoUrl);
      }
      if (logoData?.base64) {
        const maxH = 14, maxW = 45;
        let logoW, logoH;
        if (logoData.aspectRatio > maxW/maxH) {
          logoW = maxW;
          logoH = logoW / logoData.aspectRatio;
        } else {
          logoH = maxH;
          logoW = logoH * logoData.aspectRatio;
        }
        doc.addImage(logoData.base64, 'PNG', pageWidth - margin - logoW, 12, logoW, logoH, undefined, 'FAST');
      }
    } catch (e) {
      console.warn('Logo loading failed:', e);
    }
  }
};

export const drawTitleUnderline = (doc, x, y, width = 40) => {
  doc.setFillColor(...COLORS.azur);
  doc.rect(x, y, width, 2.5, 'F');
};

export const drawFooterLine = (doc, margin, pageWidth, pageHeight) => {
  doc.setDrawColor(...COLORS.azur);
  doc.setLineWidth(0.8);
  doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
};
